// Shared finance/NDIS UI primitives for Support Coordination + Plan Management.
// Currently: SupportItemPicker (NDIS price-book combobox). More primitives (Modal/Field/
// LineGrid/Stepper) will be lifted here as PM phases land.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import clsx from 'clsx';
import { Search, Check, Loader2, X, MoreVertical } from 'lucide-react';

export const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * NDIS price-book support-item combobox. Searches GET /price-books/items and, on select,
 * calls onSelect({ code, name, rate }). Display shows the chosen item + its weekday rate.
 *   props: value (support_item_code), valueName, rate, priceBookId?, onSelect, placeholder
 */
export function SupportItemPicker({ value, valueName, rate, priceBookId, category, onSelect, placeholder = 'Search NDIS price book…' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef();

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const search = useCallback((term) => {
    setLoading(true);
    const params = { search: term || '' };
    if (priceBookId) params.priceBookId = priceBookId;
    if (category) params.category = category;
    api.get('/price-books/items', { params })
      .then((r) => { setResults(Array.isArray(r.data) ? r.data.slice(0, 50) : []); setHi(0); })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [priceBookId, category]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => search(q), 220);
    return () => clearTimeout(t);
  }, [q, open, search]);

  const choose = (it) => {
    onSelect({ code: it.supportItemNumber, name: it.supportItemName, rate: it.weekdayRate });
    setOpen(false); setQ('');
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (results[hi]) choose(results[hi]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef}>
      {value ? (
        <div className="flex items-center justify-between gap-2 input-field !py-2">
          <div className="min-w-0">
            <p className="text-sm text-slate-700 truncate">{valueName || value}</p>
            <p className="text-[11px] text-slate-400 font-mono">{value}{rate != null ? ` · ${money(rate)}/unit` : ''}</p>
          </div>
          <button type="button" onClick={() => { onSelect({ code: '', name: '', rate: null }); setOpen(true); }}
            className="text-slate-400 hover:text-red-500 shrink-0" aria-label="Clear support item"><X size={15} /></button>
        </div>
      ) : (
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="input-field !pl-9" placeholder={placeholder} value={q}
            onFocus={() => { setOpen(true); if (!results.length) search(''); }}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onKeyDown={onKey} role="combobox" aria-expanded={open} aria-controls="sip-list" />
        </div>
      )}
      {open && !value && (
        <div id="sip-list" role="listbox"
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl max-h-72 overflow-y-auto">
          {loading ? (
            <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-slate-300" size={18} /></div>
          ) : results.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-slate-400">No matching support items</p>
          ) : results.map((it, idx) => (
            <button key={it.supportItemNumber} type="button" role="option" aria-selected={idx === hi}
              onMouseEnter={() => setHi(idx)} onClick={() => choose(it)}
              className={clsx('w-full text-left px-3 py-2 flex items-center gap-2 transition-colors',
                idx === hi ? 'bg-primary-50' : 'hover:bg-slate-50')}>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-700 truncate">{it.supportItemName}</p>
                <p className="text-[11px] text-slate-400 font-mono">{it.supportItemNumber}</p>
              </div>
              <span className="text-sm font-semibold text-slate-600 tabular-nums shrink-0">{it.weekdayRate != null ? money(it.weekdayRate) : '—'}</span>
              {idx === hi && <Check size={14} className="text-primary-500 shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FINANCE DESIGN TOOLKIT — shared presentational primitives so every page in the
// Financials section reads as one design language (money-first cards, kebab row
// actions, section panels, page header). Import these instead of hand-rolling.
// ─────────────────────────────────────────────────────────────────────────────

export const STAT_COLORS = {
  primary: { accent: 'bg-primary-500', chip: 'bg-primary-50 text-primary-600' },
  green:   { accent: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-600' },
  red:     { accent: 'bg-red-500',     chip: 'bg-red-50 text-red-600' },
  amber:   { accent: 'bg-amber-400',   chip: 'bg-amber-50 text-amber-600' },
  violet:  { accent: 'bg-violet-500',  chip: 'bg-violet-50 text-violet-600' },
  blue:    { accent: 'bg-blue-500',    chip: 'bg-blue-50 text-blue-600' },
  cyan:    { accent: 'bg-cyan-500',    chip: 'bg-cyan-50 text-cyan-600' },
  slate:   { accent: 'bg-slate-300',   chip: 'bg-slate-100 text-slate-500' },
};

/**
 * Money-first summary card — light theme, colour accent bar + soft icon chip and a
 * staggered entrance. `i` drives the reveal delay; `color` is a STAT_COLORS key.
 *   <StatCard i={0} label="Revenue" value="$12,400" sub="this month" icon={DollarSign} color="primary" />
 */
export function StatCard({ label, value, sub, icon: Icon, color = 'primary', i = 0 }) {
  const c = STAT_COLORS[color] || STAT_COLORS.primary;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.05, duration: 0.25, ease: 'easeOut' }}
      className="relative card px-4 py-3.5 overflow-hidden">
      <span className={clsx('absolute left-0 top-0 h-full w-1', c.accent)} />
      <div className="flex items-start justify-between gap-3 pl-1.5">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
          <p className="text-2xl font-bold text-slate-900 tabular-nums leading-tight mt-1">{value}</p>
          {sub && <p className="text-[11px] text-slate-400 mt-0.5 truncate">{sub}</p>}
        </div>
        {Icon && (
          <div className={clsx('shrink-0 w-9 h-9 rounded-xl flex items-center justify-center', c.chip)}>
            <Icon size={17} />
          </div>
        )}
      </div>
    </motion.div>
  );
}

/**
 * Page header — big page-title + subtitle on the left, action controls on the right.
 *   <FinPageHeader title="Client Rates" subtitle="…">{buttons}</FinPageHeader>
 */
export function FinPageHeader({ title, subtitle, children }) {
  return (
    <div className="flex items-start justify-between flex-wrap gap-3">
      <div className="min-w-0">
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap justify-end">{children}</div>}
    </div>
  );
}

/**
 * Section panel — a `card` with a header bar (icon + title + optional count pill +
 * right-aligned actions) and a body. Pass `bodyClass="p-4"` for padded bodies, or
 * omit for flush tables.
 */
export function SectionCard({ title, count, icon: Icon, actions, children, bodyClass = '' }) {
  return (
    <div className="card overflow-hidden">
      {(title || actions) && (
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {Icon && <Icon size={15} className="text-slate-400 shrink-0" />}
            {title && <h2 className="text-sm font-semibold text-slate-700 truncate">{title}</h2>}
            {count != null && (
              <span className="text-[11px] font-semibold text-slate-400 bg-slate-100 rounded-full px-2 py-0.5 shrink-0">{count}</span>
            )}
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </div>
      )}
      <div className={bodyClass}>{children}</div>
    </div>
  );
}

/**
 * Overflow (⋮) row-actions menu — keeps a primary action prominent while secondary
 * actions collapse into a portal popover that is never clipped by table overflow.
 *   items: [{ label, icon, onClick, danger?, disabled?, divider? }]
 */
export function RowActionsMenu({ items = [] }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const toggle = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: Math.max(8, r.right - 208) });
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const visible = items.filter(Boolean);
  if (!visible.length) return null;

  return (
    <>
      <button ref={btnRef} onClick={toggle} aria-label="More actions" title="More actions"
        className={clsx('p-1.5 rounded-lg border transition-colors',
          open ? 'bg-slate-100 border-slate-300 text-slate-700' : 'border-slate-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50')}>
        <MoreVertical size={15} />
      </button>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[59]" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div className="fixed z-[60] w-52 bg-white border border-slate-200 rounded-xl shadow-2xl py-1 text-sm"
            style={{ top: pos.top, left: pos.left }} onClick={(e) => e.stopPropagation()}>
            {visible.map((it, i) => it.divider ? (
              <div key={i} className="my-1 border-t border-slate-100" />
            ) : (
              <button key={i} disabled={it.disabled}
                onClick={() => { setOpen(false); it.onClick(); }}
                className={clsx('w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors disabled:opacity-40',
                  it.danger ? 'text-red-600 hover:bg-red-50' : 'text-slate-600 hover:bg-slate-50')}>
                {it.icon && <span className="shrink-0">{it.icon}</span>}<span>{it.label}</span>
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
    </>
  );
}
